import { BackupData, Bookmark, Tag } from '@/types';
import { getStorage, saveBookmarks, saveTags, getApiBase, isLocalMode, saveLocalData, saveSettings, saveAIPlatforms } from './storage';
import { v4 as uuidv4 } from 'uuid';

export const exportData = async (): Promise<void> => {
  const data = await getStorage();
  const exportData: BackupData = {
    version: '1.0.0',
    exportDate: new Date().toISOString(),
    bookmarks: data.bookmarks,
    tags: data.tags,
    settings: data.settings,
    aiPlatforms: data.aiPlatforms,
    quickDialItems: data.settings.quickDialItems,
    todos: data.todos || [] // Export todos
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `webtags-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
};

export const importData = async (file: File): Promise<void> => {
  const text = await file.text();
  
  try {
      if (file.name.endsWith('.json')) {
          await importJson(text);
      } else if (file.name.endsWith('.html') || file.name.endsWith('.htm')) {
          await importHtml(text);
      } else {
          throw new Error('Unsupported file type');
      }
      alert('导入成功！');
      window.location.reload();
  } catch (e) {
      console.error(e);
      alert('导入失败：' + (e instanceof Error ? e.message : '未知错误'));
  }
};

const importJson = async (text: string) => {
    const importedData = JSON.parse(text) as BackupData;
    if (!importedData.bookmarks || !importedData.tags) {
        throw new Error('Invalid backup file');
    }
    
    const currentData = await getStorage();
    
    // Merge logic: Merge by ID (overwrite existing), otherwise add new
    // Also keep URL deduplication for bookmarks if ID doesn't match but URL does?
    // User requirement: "Import data needs to be deduplicated and overwritten based on ID. If there is no match, add it."
    
    // 1. Merge Bookmarks
    const bookmarkMap = new Map(currentData.bookmarks.map(b => [b.id, b]));
    importedData.bookmarks.forEach(b => {
        bookmarkMap.set(b.id, b);
    });
    const mergedBookmarks = Array.from(bookmarkMap.values());
    
    // 2. Merge Tags
    const tagMap = new Map(currentData.tags.map(t => [t.id, t]));
    importedData.tags.forEach(t => {
        tagMap.set(t.id, t);
    });
    const mergedTags = Array.from(tagMap.values());

    // 3. Merge Todos
    const todoMap = new Map((currentData.todos || []).map(t => [t.id, t]));
    (importedData.todos || []).forEach(t => {
        todoMap.set(t.id, t);
    });
    const mergedTodos = Array.from(todoMap.values());

    if (isLocalMode()) {
        // Local Mode: Save directly to localStorage
        saveLocalData({
            bookmarks: mergedBookmarks,
            tags: mergedTags,
            todos: mergedTodos,
            // Restore Settings and AI Platforms if available in backup
            ...(importedData.settings ? { settings: importedData.settings } : {}),
            ...(importedData.aiPlatforms ? { aiPlatforms: importedData.aiPlatforms } : {})
        });
    } else {
        // Server Mode: Save via API
        const apiBase = getApiBase();
        const errors: string[] = [];

        try {
            const res = await fetch(`${apiBase}/data/tags`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(mergedTags)
            });
            if (!res.ok) throw new Error(res.status.toString());
        } catch(e: any) { errors.push(`Tags (${e.message})`); }

        try {
            const res = await fetch(`${apiBase}/data/bookmarks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(mergedBookmarks)
            });
            if (!res.ok) throw new Error(res.status.toString());
        } catch(e: any) { errors.push(`Bookmarks (${e.message})`); }
        
        try {
            const res = await fetch(`${apiBase}/data/todos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(mergedTodos)
            });
            if (!res.ok) throw new Error(res.status.toString());
        } catch(e: any) { errors.push(`Todos (${e.message})`); }
        
        if (importedData.settings) {
            try {
                const res = await fetch(`${apiBase}/data/settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(importedData.settings)
                });
                if (!res.ok) throw new Error(res.status.toString());
            } catch(e: any) { errors.push(`Settings (${e.message})`); }
        }
        
        if (importedData.aiPlatforms) {
            try {
                const res = await fetch(`${apiBase}/data/aiPlatforms`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(importedData.aiPlatforms)
                });
                if (!res.ok) throw new Error(res.status.toString());
            } catch(e: any) { errors.push(`AIPlatforms (${e.message})`); }
        }

        if (errors.length > 0) {
            throw new Error(`部分数据保存到服务端失败: ${errors.join(', ')}。请检查服务端版本是否支持。`);
        }
    }
};

const importHtml = async (html: string) => {
    console.log('Starting HTML import, length:', html.length);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const bookmarks: Bookmark[] = [];
    const tagsMap = new Map<string, Tag>(); // name -> Tag
    
    // Helper to get or create tag
    const getTagId = (tagName: string): string => {
        if (!tagsMap.has(tagName)) {
            const newTag: Tag = {
                id: uuidv4(),
                name: tagName,
                color: '#' + Math.floor(Math.random()*16777215).toString(16)
            };
            tagsMap.set(tagName, newTag);
        }
        return tagsMap.get(tagName)!.id;
    };

    // Recursive function to traverse DL/DT
    const traverse = (element: Element, currentTags: string[]) => {
        const children = element.children;
        let lastFolderName = '';
        
        // console.log('Traversing element:', element.tagName, 'Children:', children.length);

        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const tagName = child.tagName.toUpperCase();
            
            // Handle P tag which might wrap content or just be a separator
            if (tagName === 'P') {
                 if (child.children.length > 0) {
                     traverse(child, currentTags);
                 }
                 continue;
            }

            if (tagName === 'DT') {
                // DT usually contains H3 (folder title) or A (bookmark)
                // It might also contain DL (nested folder content)
                let h3 = null;
                let a = null;
                let dl = null;

                for (let j = 0; j < child.children.length; j++) {
                    const sub = child.children[j];
                    const subTagName = sub.tagName.toUpperCase();
                    if (subTagName === 'H3') h3 = sub;
                    else if (subTagName === 'A') a = sub;
                    else if (subTagName === 'DL') dl = sub;
                }

                if (h3) {
                    lastFolderName = h3.textContent || 'Uncategorized';
                    // console.log('Found Folder:', lastFolderName);
                    
                    // Create tag if not exists
                    if (!tagsMap.has(lastFolderName)) {
                         getTagId(lastFolderName);
                    }
                }

                if (a) {
                    // Extract text content recursively to get the title
                    const title = a.textContent || 'No Title';
                    const url = a.getAttribute('href') || a.getAttribute('HREF');
                    const icon = a.getAttribute('icon') || a.getAttribute('ICON');
                    const addDate = a.getAttribute('add_date') || a.getAttribute('ADD_DATE');
                    
                    if (url) {
                        // console.log('Found Bookmark:', title);
                        // getTagId handles duplicate checks and returns the ID
                        const tagIds = currentTags.map(name => getTagId(name));
                        bookmarks.push({
                            id: uuidv4(),
                            title,
                            url,
                            favicon: icon || `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}`,
                            thumbnail: '',
                            tags: tagIds,
                            createdAt: addDate ? parseInt(addDate) * 1000 : Date.now(),
                            visitCount: 0
                        });
                    } else {
                        console.warn('Bookmark missing URL:', title);
                    }
                }

                if (dl) {
                    const nextTags = lastFolderName ? [...currentTags, lastFolderName] : currentTags;
                    traverse(dl, nextTags);
                    lastFolderName = ''; // Consumed by this DL
                }
            } else if (tagName === 'DL') {
                // Sibling DL case
                const nextTags = lastFolderName ? [...currentTags, lastFolderName] : currentTags;
                traverse(child, nextTags);
                lastFolderName = ''; // Consumed by this DL
            }
        }
    };

    // Start traversal from the root DL
    const rootDl = doc.querySelector('dl'); // Case insensitive in HTML
    if (rootDl) {
        console.log('Root DL found');
        traverse(rootDl, []);
    } else {
        console.warn('No Root DL found, trying body');
        if (doc.body) traverse(doc.body, []);
    }
    
    console.log(`Parsed ${bookmarks.length} bookmarks and ${tagsMap.size} tags.`);

    // Save to storage
    const currentData = await getStorage();
    
    // Merge Tags
    const existingTagNames = new Set(currentData.tags.map(t => t.name));
    const newTagsToSave: Tag[] = [];
    
    // Map existing tag names to IDs to reuse them
    const existingTagNameToId = new Map(currentData.tags.map(t => [t.name, t.id]));

    tagsMap.forEach((tag) => {
        if (!existingTagNames.has(tag.name)) {
            newTagsToSave.push(tag);
        } else {
             // Update bookmark tags to use existing ID
             const existingId = existingTagNameToId.get(tag.name)!;
             bookmarks.forEach(b => {
                 b.tags = b.tags.map(tid => tid === tag.id ? existingId : tid);
             });
        }
    });

    const mergedTags = [...currentData.tags, ...newTagsToSave];
    
    // Merge Bookmarks
    const existingUrls = new Set(currentData.bookmarks.map(b => b.url));
    const uniqueNewBookmarks = bookmarks.filter(b => !existingUrls.has(b.url));
    const mergedBookmarks = [...currentData.bookmarks, ...uniqueNewBookmarks];

    console.log(`Saving ${mergedBookmarks.length} bookmarks (${uniqueNewBookmarks.length} new) and ${mergedTags.length} tags.`);

    if (isLocalMode()) {
        saveLocalData({
            tags: mergedTags,
            bookmarks: mergedBookmarks
        });
    } else {
        await saveTags(mergedTags);
        await saveBookmarks(mergedBookmarks);
    }
};
